import { forwardRef, useEffect, useState, type ChangeEvent, type FocusEvent } from 'react';

import {
  datetimeInputDisplayToIso,
  isoToDatetimeInputDisplay,
} from '@/shared/utils/date';

import { Input, type InputProps } from './Input';

export interface IstDateTimeFieldProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'defaultValue'> {
  /** API ISO string (or empty). */
  value: string;
  onChange: (iso: string) => void;
}

/**
 * Datetime form control: shows **dd/mm/yyyy HH:mm** (IST text).
 * Emits ISO to the API. Listings still use `formatDate`.
 */
export const IstDateTimeField = forwardRef<HTMLInputElement, IstDateTimeFieldProps>(
  function IstDateTimeField(
    { value, onChange, onBlur, disabled, placeholder = 'dd/mm/yyyy HH:mm', ...rest },
    ref,
  ) {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState(() => isoToDatetimeInputDisplay(value));

    useEffect(() => {
      if (!focused) {
        setDraft(isoToDatetimeInputDisplay(value));
      }
    }, [value, focused]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setDraft(next);
      const iso = datetimeInputDisplayToIso(next);
      if (iso === null) return;
      onChange(iso);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      const iso = datetimeInputDisplayToIso(draft);
      if (iso === null) {
        setDraft(isoToDatetimeInputDisplay(value));
      } else {
        onChange(iso);
        setDraft(isoToDatetimeInputDisplay(iso));
      }
      onBlur?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        value={focused ? draft : isoToDatetimeInputDisplay(value)}
        onFocus={() => {
          setFocused(true);
          setDraft(isoToDatetimeInputDisplay(value) || draft);
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        {...rest}
      />
    );
  },
);
